<script setup lang="ts">
import { computed } from "vue";
import { RouterLink } from "vue-router";

import type { TabItemProps } from "@/dsComponents/Tab/types";

const props = withDefaults(defineProps<TabItemProps>(), {
    isActive: false,
    hasNotif: false,
});

const tabItemTag = computed(() => (props.link ? RouterLink : "div"));
const tabItemBindings = computed(() => (props.link ? { to: props.link } : {}));
const hasCount = computed(() => props.count !== undefined && props.count !== null);

const tabItemClasses = computed(() => {
    return [
        "ds-tab-item",
        {
            "ds-tab-item--active": props.isActive,
        },
    ];
});

const tabButtonClasses = computed(() => {
    return [
        "ds-tab-item__button",
        {
            "ds-tab-item__button--notif": props.hasNotif,
        },
        props.hasNotif && props.notifColor ? `ds-tab-item__button--notif-${props.notifColor}` : "",
    ];
});
</script>

<template>
    <component :is="tabItemTag" v-bind="tabItemBindings" :class="tabItemClasses">
        <div :class="tabButtonClasses">
            <div v-if="props.icon" class="ds-tab-item__button-icon">
                <i :class="`ti ti-${props.icon}`"></i>
            </div>
            <div class="ds-tab-item__button-label">
                <span class="typo-xs-medium">
                    {{ props.label }}
                </span>
            </div>
            <div v-if="hasCount" class="ds-tab-item__button-count">
                {{ props.count }}
            </div>
        </div>
    </component>
</template>

<style lang="scss" scoped>
.ds-tab-item {
    display: flex;
    flex-direction: column;
    justify-content: center;
    position: relative;
    min-height: 48px;
    padding: var(--spacing-md, 8px) 0;
    cursor: pointer;
    text-decoration: none;
    color: inherit;

    &:after {
        content: "";
        position: absolute;
        right: 0;
        bottom: -1px;
        left: 0;
        height: 2px;
        background-color: transparent;
        transition: ease-in-out 0.2s;
    }

    &__button {
        display: flex;
        align-items: center;
        justify-content: flex-start;
        gap: var(--spacing-xs, 4px);
        min-height: 28px;
        padding: 0 var(--spacing-md, 8px);
        color: var(--text-soft-400, #9297aa);
        border-radius: var(--radius-sm, 6px);
        border: 0.5px solid var(--neutral-0, #ffffff);
        background-color: var(--bg-white-0, #ffffff);
        transition: ease-in-out 0.2s;

        &-icon {
            position: relative;
            font-size: 14px;
        }

        &-label {
            white-space: nowrap;
            position: relative;
            padding: 0 var(--spacing-xs, 4px);
        }

        &-count {
            font-family: "Inter";
            font-size: 10px;
            line-height: 14px;
            font-weight: 400;
            padding: 0 var(--spacing-xs, 4px);
            border-radius: var(--radius-xs, 4px);
            background-color: var(--bg-lightest-50, #f6f7f9);
        }

        &--notif {
            .ds-tab-item__button-icon::after,
            .ds-tab-item__button-label::after {
                content: "";
                position: absolute;
                top: -4px;
                right: -6px;
                width: 6px;
                height: 6px;
                border-radius: var(--radius-full, 9999px);
                border: 2px solid var(--neutral-0, #ffffff);
                background-color: var(--blue-base, #2051f8);
            }

            &-blue {
                .ds-tab-item__button-icon::after,
                .ds-tab-item__button-label::after {
                    background-color: var(--blue-base, #2051f8);
                }
            }

            &-green {
                .ds-tab-item__button-icon::after,
                .ds-tab-item__button-label::after {
                    background-color: var(--green-base, #00a355);
                }
            }

            &-yellow {
                .ds-tab-item__button-icon::after,
                .ds-tab-item__button-label::after {
                    background-color: var(--yellow-base, #fab305);
                }
            }

            &-red {
                .ds-tab-item__button-icon::after,
                .ds-tab-item__button-label::after {
                    background-color: var(--red-base, #e51940);
                }
            }

            &:has(.ds-tab-item__button-icon) .ds-tab-item__button-label::after {
                display: none;
            }
        }
    }

    &:hover {
        &::after {
            background-color: var(--neutral-300, #acb1c3);
        }

        .ds-tab-item__button {
            color: var(--text-main-900, #00061a);
            border: 0.5px solid var(--neutral-50, #f6f7f9);
            background-color: var(--bg-lightest-50, #f6f7f9);

            &-icon::after,
            &-label::after {
                border-color: var(--neutral-50, #f6f7f9);
            }
        }

        .ds-tab-item__button-count {
            background-color: var(--bg-weak-100, #eff2f5);
        }
    }

    &--active,
    &--active:hover {
        &::after {
            background-color: var(--bg-strong-900, #00061a);
        }

        .ds-tab-item__button {
            color: var(--text-main-900, #00061a);
            border-color: var(--stroke-soft-200, #dbdfe6);
            background-color: var(--bg-weak-100, #eff2f5);
            box-shadow: var(--regular-shadow-xs, 0px 1px 2px #dbdfe640);

            &-icon::after,
            &-label::after {
                border-color: var(--neutral-100, #eff2f5);
            }
        }

        .ds-tab-item__button-count {
            background-color: var(--bg-soft-200, #dbdfe6);
        }
    }
}
</style>
